// Pricing — the public, Writer-integrated pricing surface.
//
// One integrated product: you draft in Writer and request an AI review
// whenever you want, both paid from one metered credit balance.

import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { GraduationCap, Building2 } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { PricingHeaderV2 } from '@/components/pricing/PricingHeaderV2';
import { SubscriptionPlans } from '@/components/pricing/SubscriptionPlans';
import { TopUpPacks } from '@/components/pricing/TopUpPacks';
import { PricingFaq } from '@/components/pricing/PricingFaq';
import { EnterpriseSectionV2 } from '@/components/pricing/EnterpriseSectionV2';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const PricingV2: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Handle successful payment callback from Paddle.
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') {
      toast.success('Payment successful!', {
        description: 'Your credits have been added to your account.',
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ['userSubscription'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      navigate('/pricing', { replace: true });
    }
  }, [searchParams, navigate, queryClient]);

  return (
    <>
      <Helmet>
        <title>Pricing — Writer, AI Reviews & Credits | PaperVerse</title>
        <meta
          name="description"
          content="One plan for writing and reviewing. Draft in PaperVerse Writer and request AI reviews from a single monthly credit balance — starting free."
        />
      </Helmet>

      <Navbar />

      <main className="min-h-screen pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4">
          <PricingHeaderV2 />

          <Tabs defaultValue="researchers" className="mb-16">
            <TabsList className="justify-center mb-8">
              <TabsTrigger value="researchers" className="gap-2">
                <GraduationCap className="h-4 w-4" />
                For Researchers
              </TabsTrigger>
              <TabsTrigger value="enterprise" className="gap-2">
                <Building2 className="h-4 w-4" />
                For Conferences & Journals
              </TabsTrigger>
            </TabsList>

            <TabsContent value="researchers" className="min-h-[480px] space-y-10">
              <SubscriptionPlans />
              <TopUpPacks />
              <PricingFaq />
            </TabsContent>

            <TabsContent value="enterprise" className="min-h-[480px]">
              <EnterpriseSectionV2 />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default PricingV2;
